import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  model,
  output,
  resource,
  signal,
  untracked,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { FormArray, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ClrForm,
  ClrLoadingState,
  ClrModalModule,
  ClrFormsModule,
  ClrTooltipModule,
  ClrSpinnerModule,
  ClrAlertModule,
  ClrLoadingModule,
} from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { BaseDataRoute, BaseItem, BaseItemsHandler } from '../baseitems-wrapper.component';
import { FormGroupOfType } from 'src/app/types/shared';
import { errorHandle } from 'src/app/misc/functions';

@Component({
  selector: 'app-baseitems-sbagp-modal',
  templateUrl: './baseitems-sbagp-modal.component.html',
  styleUrl: './baseitems-sbagp-modal.component.scss',
  imports: [
    ClrModalModule,
    ClrSpinnerModule,
    ClrTooltipModule,
    ClrFormsModule,
    ClrAlertModule,
    ClrLoadingModule,
    FormsModule,
    ReactiveFormsModule,
    MrTranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class Baseitems_SBAGP_ModalComponent implements BaseItemsHandler {
  private readonly apiService = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);

  readonly refresh = output<BaseItem[]>();
  readonly isModalOpen = model<boolean>();

  readonly route = model.required<BaseDataRoute>();
  readonly preSelected = computed(() => this.route().selected);

  readonly clrForms = viewChildren(ClrForm);

  protected readonly isRep = this.apiService.isRep;
  protected readonly bezeichnung = new FormControl('', [Validators.required, Validators.pattern(/^[^\s\d]+/)]);

  protected readonly isNeu = signal(false);
  protected readonly isRename = linkedSignal(() => this.isNeu() || this.isCopy());
  protected readonly selectedName = signal<string>(undefined);

  protected readonly isCopy = signal(false);
  protected readonly isImported = linkedSignal(() => (this.pruefarten(), false));

  private readonly namesResource = resource({
    params: () => this.isModalOpen() || undefined,
    loader: () => this.apiService.getBaseItems('SBAGP'),
    defaultValue: [],
  });
  protected readonly names = this.namesResource.value;
  protected readonly isLoadingNames = this.namesResource.isLoading;

  private readonly pruefartenResource = resource({
    params: () => (this.isNeu() ? undefined : this.selectedName() || undefined),
    loader: ({ params: name }) => this.apiService.getBaseItems('SBAGP', name),
    defaultValue: [],
  });
  protected readonly pruefarten = this.pruefartenResource.value;
  protected readonly isLoadingPruefarten = this.pruefartenResource.isLoading;

  protected readonly loadState = signal(ClrLoadingState.DEFAULT);
  private isChanged = false;

  protected readonly form = new FormArray<FormGroupOfType<SBAGP_Item>>([], Validators.required);
  private get emptyGroup() {
    return new FormGroupOfType<SBAGP_Item>({
      ID: new FormControl<number>(null),
      Pruefart: new FormControl<string>(null, Validators.required),
      Anzahl: new FormControl<number>(null, [Validators.required, Validators.min(0)]),
    });
  }

  constructor() {
    const autoSelectFirst = effect(() => {
      const firstBaugruppe = this.names()[0];
      if (!firstBaugruppe) return;
      this.selectedName.set(firstBaugruppe);
      autoSelectFirst.destroy();
    });

    effect(() => {
      const preSelect = this.preSelected();
      if (preSelect && typeof preSelect == 'string' && !this.isLoadingNames())
        untracked(() => {
          autoSelectFirst.destroy();
          this.route.update(route => ({ ...route, selected: null }));
          const found = this.names().find(name => name == preSelect);
          this.selectedName.set(found ?? preSelect);
          if (!found) {
            this.isNeu.set(true);
          }
        });
    });
    effect(() => {
      if (this.isCopy()) {
        this.bezeichnung.setValue(`${this.selectedName()} - ${this.mrTranslate.transform('Kopie')}`);
        for (const group of this.form.controls) {
          group.controls.ID.setValue(null);
        }
      } else {
        this.bezeichnung.reset(this.selectedName());
        this.form.clear();
        if (this.pruefarten().length) this.importInForm(this.pruefarten());
      }
    });
    effect(() => {
      const status = this.namesResource.status();
      untracked(() => {
        if (status == 'reloading') {
          this.isChanged = true;
        } else if (status == 'resolved' && this.isChanged) {
          this.isChanged = false;
          this.refresh.emit(this.names().map<BaseItem>(name => ({ Bezeichnung: name, ID: null })));
        }
      });
    });
  }

  private importInForm(pruefarten: SBAGP_Item[]) {
    for (const pruefart of pruefarten) {
      const group = this.emptyGroup;
      group.reset(pruefart);
      this.form.push(group);
    }
    this.isImported.set(true);
  }

  protected addPruefart() {
    this.form.push(this.emptyGroup);
  }

  protected async addDefaultPruefarts() {
    const pruefarten = await this.apiService.getBaseItems('SBAGP', '0');
    this.importInForm(pruefarten);
  }

  protected async renameBaugruppe() {
    this.loadState.set(ClrLoadingState.LOADING);

    try {
      const name = this.bezeichnung.value;
      await this.apiService.renameBaugruppe(this.selectedName(), name);

      this.loadState.set(ClrLoadingState.SUCCESS);
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      this.namesResource.reload();
      this.selectedName.set(name);
      this.isRename.set(false);
    } catch (er) {
      this.loadState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async onSubmit() {
    if (this.form.invalid) {
      this.clrForms().forEach(form => form.markAsTouched());
      return;
    }

    this.loadState.set(ClrLoadingState.LOADING);
    const sendObj: SBAGP_Item[] = this.form.getRawValue();
    const name = this.bezeichnung.value;

    try {
      await this.apiService.setBaugruppe(name, sendObj);

      this.loadState.set(ClrLoadingState.SUCCESS);
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert'));
      if (this.isRename()) {
        this.isRename.set(false);
        this.isNeu.set(false);
        this.isCopy.set(false);
        this.namesResource.reload();
        this.selectedName.set(name);
      }
      if (name == this.selectedName()) this.pruefartenResource.reload();
    } catch (er) {
      this.loadState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deleteBaugruppe() {
    try {
      await this.apiService.deleteBaseItems('SBAGP', this.selectedName());

      this.toastr.success(this.mrTranslate.transform('Eintrag wurde gelöscht'));
      this.namesResource.reload();
      this.selectedName.set(undefined);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async deletePruefart(index: number) {
    const group = this.form.value[index];
    if (!group) return;

    try {
      if (group.ID) {
        await this.apiService.deleteBaseItems('SBAGP', group.ID);
        this.toastr.success(this.mrTranslate.transform('Eintrag wurde gelöscht'));
        this.pruefarten.update(arr => (arr.splice(index, 1), [...arr]));
      }

      this.form.removeAt(index);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }
}

export interface SBAGP_Item {
  ID: number;
  Pruefart: string;
  Anzahl: number;
}
